// https://github.com/pmndrs/zustand
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { persist } from 'zustand/middleware'
import insightsApi from '../api/insightsApi'

// helper to isolate chat state per report + channel
const getAskKey = (reportId, channel) => `${reportId}::${channel}`

const useStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        currentSurvey: null,
        csrf: '',
        surveys: [],
        surveyPage: 0,
        surveysPerPage: 9,
        new: {},
        authenticated: false,
        previewData: null,
        answers: {},
        currentPage: 1,
        rowsPerPage: 10,
        totalItems: 0,
        activeUser: null,
        language: 'en',

        setActiveUser: (user) => set({ activeUser: user }),
        setAnswers: (val) => set({ answers: val }),
        setPreviewData: (val) => set({ previewData: val }),
        setAuthenticated: (val) => set({ authenticated: !!val }),
        setCurrentSurvey: (survey) => set({ currentSurvey: survey }),
        setSurveys: (surveys) => set({ surveys }),
        setCurrentPage: (page) => set({ currentPage: page }),
        setRowsPerPage: (rows) => set({ rowsPerPage: rows }),
        setTotalItems: (total) => set({ totalItems: total }),
        setLanguage: (lang) => set({ language: lang }),

        // ASK-PROMPTA (IN-MEMORY)
        askPrompta: {},

        // Create a new session
        initAskPromptaSession: (reportId, channel) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: {
                  sessionId: crypto.randomUUID(),
                  messages: [],
                  qaHistory: [],
                  isViewingHistory: false,
                },
              },
            }
          }),

        appendUserMessage: (reportId, channel, question) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const current = state.askPrompta[key]
            if (!current) return {}

            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: {
                  ...current,
                  messages: [
                    ...current.messages,
                    { role: 'user', content: question, createdAt: Date.now() },
                  ],
                  qaHistory: [
                    ...current.qaHistory,
                    { question, answer: '' },
                  ],
                },
              },
            }
          }),
        
        // Seed a QA-history entry without rendering a user bubble. Quick
        // prompts send no user-visible question, so their answer had no
        // qaHistory slot to accumulate into and was missing from the history
        // sent to the RAG socket on the next follow-up question.
        appendQaHistoryEntry: (reportId, channel, question) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const current = state.askPrompta[key]
            if (!current) return {}

            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: {
                  ...current,
                  qaHistory: [...current.qaHistory, { question, answer: '' }],
                },
              },
            }
          }),

        appendAssistantMessage: (reportId, channel) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const current = state.askPrompta[key]
            if (!current) return {}
            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: {
                  ...current,
                   messages: [
                    ...current.messages,
                    {
                      role: 'assistant',
                      content: '',
                      isStreaming: true,
                      messageId: crypto.randomUUID(),
                      createdAt: Date.now(),
                      streamStartedAt: Date.now(),
                      // frames that landed before the message existed —
                      // citations and risk-meta both arrive ahead of the
                      // first answer token
                      ...(current.pendingDocuments
                        ? { documents: current.pendingDocuments }
                        : {}),
                      ...(current.pendingRiskMeta
                        ? { riskMeta: current.pendingRiskMeta }
                        : {}),
                    },
                  ],
                  pendingDocuments: null,
                  pendingRiskMeta: null,
                },
              },
            }
          }),

        appendAssistantChunk: (reportId, channel, chunk) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const current = state.askPrompta[key]
            if (!current) return {}

            const messages = [...current.messages]
            const last = messages[messages.length - 1]

            if (last && last.role === 'assistant' && last.isStreaming) {
              messages[messages.length - 1] = {
                ...last,
                content: last.content + chunk,
              }
            } else if (chunk) {
              // never start a message from an empty chunk — keep-alive /
              // terminator frames parse to '' and used to push an empty
              // assistant bubble (rendered as a blank white square)
              messages.push({
                role: 'assistant',
                content: chunk,
                isStreaming: true,
                messageId: crypto.randomUUID(),
                createdAt: Date.now(),
                streamStartedAt: Date.now(),
                // pick up retrieval documents that arrived before the first chunk
                ...(current.pendingDocuments
                  ? { documents: current.pendingDocuments }
                  : {}),
                // pick up risk-meta (score cards) that arrived before the first chunk
                ...(current.pendingRiskMeta
                  ? { riskMeta: current.pendingRiskMeta }
                  : {}),
              })
            } else {
              return {}
            }

            const qaHistory = [...current.qaHistory]
            if (qaHistory.length > 0) {
              qaHistory[qaHistory.length - 1].answer += chunk
            }
            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: {
                  ...current,
                  messages,
                  qaHistory,
                  pendingDocuments: null,
                  pendingRiskMeta: null,
                },
              },
            }
          }),

        // Attach RAG retrieval documents (citations) to the assistant message
        // being streamed; held as pending if no assistant message exists yet.
        attachAssistantDocuments: (reportId, channel, documents) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const current = state.askPrompta[key]
            if (!current || !documents?.length) return {}

            const messages = [...current.messages]
            const last = messages[messages.length - 1]

            if (last && last.role === 'assistant') {
              last.documents = documents
              return {
                askPrompta: {
                  ...state.askPrompta,
                  [key]: { ...current, messages },
                },
              }
            }

            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: { ...current, pendingDocuments: documents },
              },
            }
          }),

        // Attach risk-summary meta (per-pillar scores for the score cards) to
        // the assistant message being streamed; held as pending if no assistant
        // message exists yet (risk_meta arrives before the first answer token).
        attachAssistantRiskMeta: (reportId, channel, riskMeta) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const current = state.askPrompta[key]
            if (!current || !riskMeta) return {}

            const messages = [...current.messages]
            const last = messages[messages.length - 1]

            if (last && last.role === 'assistant') {
              last.riskMeta = riskMeta
              return {
                askPrompta: {
                  ...state.askPrompta,
                  [key]: { ...current, messages },
                },
              }
            }

            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: { ...current, pendingRiskMeta: riskMeta },
              },
            }
          }),

        finalizeAssistantMessage: (reportId, channel) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const current = state.askPrompta[key]
            if (!current) return {}

            // Clear isStreaming on EVERY message (not just the last): a
            // stray never-finalized streaming message permanently hides the
            // typing indicator (`!messages.some(m => m.isStreaming)`).
            // Also drop empty assistant messages — they render as blank
            // white bubbles.
            const lastIndex = current.messages.length - 1
            const messages = current.messages
              .map((m, i) => {
                if (m.role !== 'assistant') return m
                if (!m.isStreaming && i !== lastIndex) return m
                const next = { ...m, isStreaming: false }
                if (i === lastIndex) {
                  if (next.streamStartedAt && next.latencyMs == null) {
                    next.latencyMs = Date.now() - next.streamStartedAt
                  }
                  if (!next.documents && current.pendingDocuments) {
                    next.documents = current.pendingDocuments
                  }
                  if (!next.riskMeta && current.pendingRiskMeta) {
                    next.riskMeta = current.pendingRiskMeta
                  }
                }
                return next
              })
              .filter((m) => !(m.role === 'assistant' && !m.content))

            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: {
                  ...current,
                  messages,
                  pendingDocuments: null,
                  pendingRiskMeta: null,
                },
              },
            }
          }),

        // Backfill a messageId for messages created before feedback shipped
        // (e.g. history-loaded sessions). Returns the id.
        ensureMessageId: (reportId, channel, messageIndex) => {
          const key = getAskKey(reportId, channel)
          const existing =
            get().askPrompta[key]?.messages?.[messageIndex]?.messageId
          if (existing) return existing

          const messageId = crypto.randomUUID()
          set((state) => {
            const current = state.askPrompta[key]
            if (!current || !current.messages[messageIndex]) return {}

            const messages = current.messages.map((m, i) =>
              i === messageIndex ? { ...m, messageId } : m
            )
            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: { ...current, messages },
              },
            }
          })
          return messageId
        },

        // Optimistically record a 👍/👎 vote and POST it to the feedback
        // endpoint; reverts the local state if the request fails.
        setMessageFeedback: (reportId, channel, messageId, vote) => {
          const key = getAskKey(reportId, channel)

          const applyVote = (value) =>
            set((state) => {
              const current = state.askPrompta[key]
              if (!current) return {}

              const messages = current.messages.map((m) =>
                m.messageId === messageId ? { ...m, feedback: value } : m
              )
              return {
                askPrompta: {
                  ...state.askPrompta,
                  [key]: { ...current, messages },
                },
              }
            })

          const previous =
            get().askPrompta[key]?.messages?.find(
              (m) => m.messageId === messageId
            )?.feedback ?? null

          applyVote(vote)

          insightsApi
            .postChatFeedback({
              message_id: messageId,
              vote,
              report_id: reportId,
              channel,
              comment: null,
            })
            .catch(() => {
              // silent revert — no toast for now per supervisor scope
              applyVote(previous)
            })
        },

        // Load history and make it the active session
        loadHistorySession: (reportId, channel, sessionId, messages, qaHistory) =>
          set((state) => {
            const key = getAskKey(reportId, channel)

            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: {
                  sessionId,
                  messages,
                  qaHistory,
                  isViewingHistory: true,
                },
              },
            }
          }),

        // clear UI messages only, keep session + history
        clearAskPromptaMessages: (reportId, channel) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const current = state.askPrompta[key]
            if (!current) return {}

            return {
              askPrompta: {
                ...state.askPrompta,
                [key]: {
                  ...current,
                  messages: [],
                  isViewingHistory: false,
                },
              },
            }
          }),

        resetAskPrompta: (reportId, channel) =>
          set((state) => {
            const key = getAskKey(reportId, channel)
            const copy = { ...state.askPrompta }
            delete copy[key]
            return { askPrompta: copy }
          }),
      }),
      {
        name: 'prompta-store',
        partialize: (state) => ({
          authenticated: state.authenticated,
          activeUser: state.activeUser,
          language: state.language,
        }),
      }
    )
  )
)

export default useStore
